// src/Pages/Aboutus.jsx
import React from 'react';
import { Box, Container, Typography, Grid, useTheme, useMediaQuery } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { Network, MonitorSmartphone, GraduationCap, Briefcase, Users, Server, BookOpen, UserCheck } from 'lucide-react';
import Layout from '../component/layout/Layout';

// Color tokens
const BLUE_DARK = '#0b3161';
const BLUE_LIGHT = '#e6f0fa';
const ACCENT_YELLOW = '#fdd649';
const ACCENT_YELLOW_DARK = '#f7c42d';
const WHITE = '#ffffff';
const OFF_WHITE = '#f8fafc';
const TEXT_PRIMARY = '#1e293b';
const TEXT_SECONDARY = '#475569';

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const moveShape = keyframes`
  0% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(30px, -50px) scale(1.1); }
  66% { transform: translate(-20px, 20px) scale(0.9); }
  100% { transform: translate(0, 0) scale(1); }
`;

// Styled Components
const PageWrapper = styled(Box)({
  background: WHITE,
  overflow: 'hidden',
  fontFamily: "'Inter', 'Roboto', sans-serif",
});

const Section = styled(Box)(({ theme, bgColor }) => ({
  backgroundColor: bgColor || WHITE,
  padding: '80px 0',
  animation: `${fadeIn} 0.8s ease-out`,
  [theme.breakpoints.down('md')]: {
    padding: '60px 0',
  },
}));

const SectionTitle = styled(Typography)(({ theme, centered }) => ({
  fontSize: 'clamp(2rem, 4vw, 2.5rem)',
  fontWeight: 800,
  color: BLUE_DARK,
  marginBottom: 24,
  position: 'relative',
  display: 'inline-block',
  textAlign: centered ? 'center' : 'left',
  width: centered ? '100%' : 'auto',
  lineHeight: 1.2,
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -10,
    left: centered ? '50%' : 0,
    transform: centered ? 'translateX(-50%)' : 'none',
    width: 60,
    height: 4,
    background: ACCENT_YELLOW,
    borderRadius: 2,
  },
}));

const TextBody = styled(Typography)({
  fontSize: '1.1rem',
  color: TEXT_SECONDARY,
  lineHeight: 1.8,
  marginBottom: 20,
  fontWeight: 400,
});

const HighlightNode = styled('span')({
  color: BLUE_DARK,
  fontWeight: 700,
});

// Card Components
const FeatureBox = styled(Box)(({ theme }) => ({
  background: WHITE,
  padding: '32px 28px',
  borderRadius: 16,
  height: '100%',
  border: '1px solid rgba(11, 49, 97, 0.08)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.03)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  position: 'relative',
  overflow: 'hidden',
  zIndex: 1,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: 4,
    background: `linear-gradient(90deg, ${ACCENT_YELLOW}, ${ACCENT_YELLOW_DARK})`,
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 40px rgba(11, 49, 97, 0.08)',
    borderColor: 'transparent',
    '&::before': { opacity: 1 },
    '& .icon-wrapper': {
      background: BLUE_DARK,
      color: WHITE,
    }
  },
}));

const IconWrapper = styled(Box)({
  width: 64,
  height: 64,
  borderRadius: 12,
  background: BLUE_LIGHT,
  color: BLUE_DARK,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.3s ease',
  marginBottom: 8,
});

const ListContainer = styled(Box)({
  marginTop: 32,
});

const ListItem = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 16,
  marginBottom: 20,
  padding: '16px',
  background: WHITE,
  borderRadius: 12,
  border: '1px solid rgba(11, 49, 97, 0.05)',
  transition: 'transform 0.2s ease',
  '&:hover': {
    transform: 'translateX(8px)',
    borderColor: ACCENT_YELLOW,
    boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
  }
});

// Ecosystem Card
const EcosystemCard = styled(Box)({
  textAlign: 'center',
  padding: '32px 24px',
  background: WHITE,
  borderRadius: 20,
  height: '100%',
  border: '1px solid rgba(11, 49, 97, 0.06)',
  transition: 'all 0.4s ease',
  '&:hover': {
    transform: 'scale(1.02)',
    boxShadow: '0 20px 40px rgba(11, 49, 97, 0.08)',
    borderBottom: `4px solid ${ACCENT_YELLOW}`,
  }
});

const CircleIcon = styled(Box)({
  width: 80,
  height: 80,
  borderRadius: '50%',
  background: `linear-gradient(135deg, ${WHITE} 0%, ${BLUE_LIGHT} 100%)`,
  border: `2px solid ${ACCENT_YELLOW}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto 24px auto',
  color: BLUE_DARK,
  boxShadow: '0 8px 16px rgba(253, 214, 73, 0.2)',
});

export default function AboutUs() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Layout>
      <PageWrapper>

        {/* 1. About SkillServe */}
        <Section bgColor={WHITE} sx={{ paddingTop: '100px' }}>
          <Container maxWidth="lg">
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={10}>
                <Box>
                  <Typography sx={{ color: ACCENT_YELLOW_DARK, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', mb: 1 }}>
                    Who We Are
                  </Typography>
                  <SectionTitle>About SkillServe</SectionTitle>
                  <Box sx={{ mt: 4 }}>
                    <TextBody sx={{ fontSize: '1.2rem', color: TEXT_PRIMARY, fontWeight: 500 }}>
                      <HighlightNode>SkillServe</HighlightNode> is a future-ready Learning Management System (LMS) developed by <HighlightNode>Amass Skill Ventures Private Limited</HighlightNode>, focused on enabling skilling, upskilling, and reskilling through digital learning.
                    </TextBody>
                    <TextBody>
                      Our platform connects learners, organizations, and industry experts through structured programs designed to build practical and job-relevant capabilities.
                    </TextBody>
                    <TextBody>
                      SkillServe works across the learning ecosystem, supporting individuals starting their careers as well as organizations looking to build a stronger, more capable workforce.
                    </TextBody>
                    <TextBody>
                      Through expert-designed courses, industry-aligned curriculum, and scalable learning delivery, SkillServe helps bridge the gap between education and employment.
                    </TextBody>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Section>

        {/* 2. What SkillServe Enables */}
        <Section bgColor={OFF_WHITE}>
          <Container maxWidth="lg">
            <Box mb={6} textAlign="center">
              <SectionTitle centered>What SkillServe Enables</SectionTitle>
            </Box>
            <Grid container spacing={4}>
              <Grid item xs={12} md={4}>
                <FeatureBox>
                  <IconWrapper className="icon-wrapper">
                    <MonitorSmartphone size={32} />
                  </IconWrapper>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: BLUE_DARK, fontSize: '1.25rem' }}>
                    Digital Learning Platform
                  </Typography>
                  <Typography sx={{ color: TEXT_SECONDARY, lineHeight: 1.6 }}>
                    A centralized LMS platform that enables structured course delivery, progress tracking, and digital learning access from anywhere.
                  </Typography>
                </FeatureBox>
              </Grid>
              <Grid item xs={12} md={4}>
                <FeatureBox>
                  <IconWrapper className="icon-wrapper">
                    <UserCheck size={32} />
                  </IconWrapper>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: BLUE_DARK, fontSize: '1.25rem' }}>
                    Industry Expert Programs
                  </Typography>
                  <Typography sx={{ color: TEXT_SECONDARY, lineHeight: 1.6 }}>
                    Courses developed with subject matter experts to ensure learning remains practical, relevant, and aligned with real industry needs.
                  </Typography>
                </FeatureBox>
              </Grid>
              <Grid item xs={12} md={4}>
                <FeatureBox>
                  <IconWrapper className="icon-wrapper">
                    <Network size={32} />
                  </IconWrapper>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: BLUE_DARK, fontSize: '1.25rem' }}>
                    Skill Development Ecosystem
                  </Typography>
                  <Typography sx={{ color: TEXT_SECONDARY, lineHeight: 1.6 }}>
                    Supporting learners and organizations through training programs designed to build real workplace capabilities.
                  </Typography>
                </FeatureBox>
              </Grid>
            </Grid>
          </Container>
        </Section>

        {/* 3. SkillServe For Organizations */}
        <Section bgColor={WHITE}>
          <Container maxWidth="lg">
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={6}>
                <SectionTitle>SkillServe For Organizations</SectionTitle>
                <Box sx={{ mt: 3 }}>
                  <TextBody>
                    <HighlightNode>SkillServe</HighlightNode> provides organizations with a powerful platform to manage workforce learning and training programs.
                  </TextBody>
                  <TextBody>
                    Through our LMS ecosystem, companies can deliver structured learning, track training progress, and scale skill development initiatives across teams.
                  </TextBody>
                  <TextBody>
                    Our platform supports organizations in building consistent learning systems that help employees upgrade their capabilities and stay aligned with evolving industry requirements.
                  </TextBody>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{
                  background: BLUE_LIGHT,
                  borderRadius: 4,
                  p: isMobile ? 3 : 5,
                  border: `1px solid rgba(11, 49, 97, 0.1)`
                }}>
                  <Typography sx={{ fontWeight: 700, color: BLUE_DARK, fontSize: '1.2rem', mb: 1 }}>
                    Organizations can use SkillServe for:
                  </Typography>
                  <ListContainer>
                    {[
                      { icon: <Briefcase size={24} />, text: 'Employee onboarding programs' },
                      { icon: <Server size={24} />, text: 'Compliance and certification training' },
                      { icon: <MonitorSmartphone size={24} />, text: 'Technical skill development programs' },
                      { icon: <Users size={24} />, text: 'Partner and distributor training' },
                      { icon: <GraduationCap size={24} />, text: 'Continuous workforce upskilling' },
                    ].map((item, idx) => (
                      <ListItem key={idx}>
                        <Box sx={{ color: ACCENT_YELLOW_DARK, mt: 0.5 }}>{item.icon}</Box>
                        <Typography sx={{ fontWeight: 600, color: TEXT_PRIMARY, fontSize: '1.05rem' }}>
                          {item.text}
                        </Typography>
                      </ListItem>
                    ))}
                  </ListContainer>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Section>

        {/* 4. Digital Learning That Scales */}
        <Section sx={{
          background: `linear-gradient(135deg, ${BLUE_DARK} 0%, #061e3d 100%)`,
          color: WHITE,
          position: 'relative',
          overflow: 'hidden',
          padding: '100px 0',
        }}>
          {/* Animated Background Shapes */}
          <Box sx={{
            position: 'absolute',
            top: '-20%',
            right: '10%',
            width: '400px',
            height: '400px',
            background: `radial-gradient(circle, rgba(253, 214, 73, 0.15) 0%, transparent 70%)`,
            borderRadius: '50%',
            animation: `${moveShape} 15s ease-in-out infinite alternate`,
            pointerEvents: 'none',
          }} />
          <Box sx={{
            position: 'absolute',
            bottom: '-10%',
            left: '5%',
            width: '300px',
            height: '300px',
            background: `radial-gradient(circle, rgba(230, 240, 250, 0.1) 0%, transparent 70%)`,
            borderRadius: '50%',
            animation: `${moveShape} 20s ease-in-out infinite alternate-reverse`,
            pointerEvents: 'none',
          }} />

          <Container maxWidth="md" sx={{ position: 'relative', zIndex: 2 }}>
            <Box textAlign="center">
              <Typography sx={{
                color: ACCENT_YELLOW,
                fontWeight: 800,
                letterSpacing: 2,
                textTransform: 'uppercase',
                mb: 2,
                fontSize: '0.95rem'
              }}>
                • Learning Delivered Through Technology •
              </Typography>
              <Typography sx={{
                fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
                fontFamily: "'Playfair Display', 'Merriweather', serif",
                fontWeight: 800,
                mb: 4,
                lineHeight: 1.15,
                background: `linear-gradient(90deg, #ffffff 0%, #e2e8f0 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}>
                Digital Learning That Scales
              </Typography>
              <Typography sx={{ fontSize: '1.25rem', color: '#e2e8f0', lineHeight: 1.8, mb: 3, fontWeight: 300 }}>
                <strong style={{ color: WHITE, fontWeight: 600 }}>SkillServe</strong> combines industry expertise with modern digital infrastructure to make learning accessible, measurable, and scalable.
              </Typography>
              <Typography sx={{ fontSize: '1.15rem', color: '#cbd5e1', lineHeight: 1.8, mb: 3, fontWeight: 300 }}>
                Through our LMS platform, organizations and individuals can access structured learning modules, track performance, and build skills through flexible digital learning experiences.
              </Typography>
              <Typography sx={{ fontSize: '1.15rem', color: '#cbd5e1', lineHeight: 1.8, fontWeight: 300 }}>
                The platform enables seamless course delivery, centralized learning management, and data-driven insights that support effective workforce development.
              </Typography>
            </Box>
          </Container>
        </Section>

        {/* 5. Our Learning Ecosystem */}
        <Section bgColor={OFF_WHITE} sx={{ pb: 0, paddingBottom: 0 }}>
          <Container maxWidth="lg" sx={{ pb: 10 }}>
            <Box mb={6} textAlign="center">
              <SectionTitle centered>Our Learning Ecosystem</SectionTitle>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <EcosystemCard>
                  <CircleIcon>
                    <UserCheck size={36} />
                  </CircleIcon>
                  <Typography sx={{ fontWeight: 800, color: BLUE_DARK, fontSize: '1.2rem', mb: 1.5 }}>
                    Industry Experts
                  </Typography>
                  <Typography sx={{ color: TEXT_SECONDARY, fontSize: '0.95rem', lineHeight: 1.6 }}>
                    Subject matter experts designing practical, industry-relevant learning programs.
                  </Typography>
                </EcosystemCard>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <EcosystemCard>
                  <CircleIcon>
                    <BookOpen size={36} />
                  </CircleIcon>
                  <Typography sx={{ fontWeight: 800, color: BLUE_DARK, fontSize: '1.2rem', mb: 1.5 }}>
                    Structured Curriculum
                  </Typography>
                  <Typography sx={{ color: TEXT_SECONDARY, fontSize: '0.95rem', lineHeight: 1.6 }}>
                    Courses designed with a focus on real workplace requirements.
                  </Typography>
                </EcosystemCard>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <EcosystemCard>
                  <CircleIcon>
                    <MonitorSmartphone size={36} />
                  </CircleIcon>
                  <Typography sx={{ fontWeight: 800, color: BLUE_DARK, fontSize: '1.2rem', mb: 1.5 }}>
                    Digital LMS Platform
                  </Typography>
                  <Typography sx={{ color: TEXT_SECONDARY, fontSize: '0.95rem', lineHeight: 1.6 }}>
                    Technology-driven learning delivery with centralized management.
                  </Typography>
                </EcosystemCard>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <EcosystemCard>
                  <CircleIcon>
                    <Users size={36} />
                  </CircleIcon>
                  <Typography sx={{ fontWeight: 800, color: BLUE_DARK, fontSize: '1.2rem', mb: 1.5 }}>
                    Organizations & Learners
                  </Typography>
                  <Typography sx={{ color: TEXT_SECONDARY, fontSize: '0.95rem', lineHeight: 1.6 }}>
                    Individuals and teams building skills for future opportunities.
                  </Typography>
                </EcosystemCard>
              </Grid>
            </Grid>
          </Container>
        </Section>

      </PageWrapper>
    </Layout>
  );
}
